// Drawer_NCO.tsx (refactored with DrawerLayout)
import { useQueryClient } from '@tanstack/react-query'
import { useGetChecklists } from 'api/aws/checklists/useGetChecklists'
import { useGetOrgChecklists } from 'api/aws/checklists/useGetOrgChecklists'
import { useEntitiesFromSearch } from 'api/search'
import { useSyncQueryCache } from 'api/aws/organizations_table/useSyncQueryCache'
import { useUpdateDealItems } from 'api/aws/organizations_table/useUpdateDealItems'
import { useUpdateFields } from 'api/aws/organizations_table/useUpdateFields'
import { useUserItems } from 'api/aws/users/useUserItems'
import { useCreateGoogleDoc } from 'api/google/useCreateGoogleDoc'
import { useGoogleOrganizationFolder } from 'api/google/useGoogleOrganizationFolder'
import { useValidateGoogleToken } from 'api/google/useValidateGoogleToken'
import NcoGridHeader from 'components/NCO/NcoGridHeader'
import TabRouting from 'components/Tabs/TabRouting'
import { useNcoDrawerOpen } from 'context/DrawerProvider/useDrawerState'
import { useSnackbarContext } from 'context/SnackbarProvider/SnackbarProvider'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { DrawerLayout } from 'theme/components'
import { Checklist, DrawerBaseProps, DrawerWrapperInjectedProps, NewDeal, OrganizationTableItem, User } from 'types'
import { addChannelsToDeal } from 'utils/addChannelsToDeals'
import { getGoogleEditorUrl } from 'utils/getGoogleEditorUrl'
import { buildNcoTabsConfig } from './config'
import { determineSeoTier } from './determineSeoTier'
import ExpandDisplay from './ExpandDisplay'
import { useNcoHelpers } from './useNcoHelpers'

type CompletedCounts = {
  onboardingprogress: number
  completeseoform: number
  fbpage: number
  tiktok: number
  linkedin: number
  microsoft: number
  spotify: number
  youtube: number
  ga4: number
  gtm: number
  mia: number
}

type ChannelKey =
  | 'gtm'
  | 'ga4'
  | 'fbpage'
  | 'completeseoform'
  | 'tiktok'
  | 'linkedin'
  | 'microsoft'
  | 'spotify'
  | 'youtube'

type Props = DrawerBaseProps &
  DrawerWrapperInjectedProps & {
    onRequestClose?: () => void
  }

const Drawer_NCO: React.FC<Props> = ({ onRequestClose, apiRef: _apiRef }) => {
  const { PK, SK } = useParams()
  const dealId = SK?.split('DEAL_')?.[1]

  const isNcoDrawerOpen = useNcoDrawerOpen()
  const { showSnackbar } = useSnackbarContext()
  const { data: users } = useUserItems()
  const queryClient = useQueryClient()

  // Persist-on-close: pending deal updates
  const [pendingDealUpdates, setPendingDealUpdates] = useState<Record<string, any>>({})
  // Track if user made explicit changes (not just computed progress updates)
  const [hasUserChanges, setHasUserChanges] = useState(false)
  const { mutateAsync: updateDealItemsAsync } = useUpdateDealItems()
  const { syncDealFields, patchOrganizationFieldsInCache } = useSyncQueryCache()
  const { mutate: updateFields } = useUpdateFields()

  // Google token helper
  const { validateToken, isValid } = useValidateGoogleToken()
  const handleValidateToken = useCallback(
    (token?: string | null, userId?: string | null) => {
      if (!token || !isValid) {
        validateToken(
          { accessToken: token ?? '', refreshToken: '', userId: userId ?? '' },
          {
            onSuccess: data => {
              if (data.isValid) localStorage.setItem('google_access_token', data.accessToken || '')
            },
          }
        )
      }
    },
    [isValid, validateToken]
  )

  // Entities
  const { organizations, deals, products, allTasks, commentsTable } = useEntitiesFromSearch()
  const commentCount = useMemo(() => {
    if (!commentsTable) return null
    return commentsTable?.filter(c => c?.PK === PK && c?.context === 'nco')?.length ?? null
  }, [commentsTable, PK])

  const organization = organizations?.find(o => o?.PK === PK) ?? null

  const currentDeal = useMemo(() => {
    if (!deals || !deals?.length || !SK) return null
    return deals?.find(d => d?.SK === SK) ?? null
  }, [deals, SK])

  const { AM_USERS, DMS_USERS, SEO_USERS, INTEGRATION_USERS } = useMemo(() => {
    const listIds: string[] = Array.isArray(currentDeal?.onboardingTeamIds) ? currentDeal.onboardingTeamIds : []
    const list: User[] = (users ?? []).filter(u => listIds.includes(u.user_uuid))
    const pick = (role: string) => list.filter(m => String(m?.role ?? '').toUpperCase() === role)

    return {
      AM_USERS: pick('AM'),
      DMS_USERS: pick('DMS'),
      SEO_USERS: pick('SEO'),
      INTEGRATION_USERS: pick('INTEGRATION'),
    }
  }, [currentDeal?.onboardingTeamIds, users])

  // Related products
  const relatedProducts: OrganizationTableItem[] = useMemo(() => {
    if (!products || !dealId || !products?.length || !PK || !SK) return []
    return products.filter(p => p?.deal_uuid === dealId)
  }, [PK, SK, dealId, products])

  // Expanded state
  const [expanded, setExpanded] = useState(false)

  const deal = useMemo(() => (currentDeal ? addChannelsToDeal(currentDeal, relatedProducts) : null), [
    currentDeal,
    relatedProducts,
  ])

  const seoTier = useMemo(() => {
    if (!relatedProducts || !relatedProducts?.length) return 0
    return determineSeoTier(relatedProducts) ?? 0
  }, [relatedProducts])

  // Google Org Folder
  useGoogleOrganizationFolder(organization?.org_uuid)

  // Checklists
  const { data: fetchedChecklists, isLoading: isChecklistsLoading } = useGetChecklists(currentDeal)
  const { data: orgChecklists } = useGetOrgChecklists(organization)

  const [checklists, setChecklists] = useState<any[]>([])
  useEffect(() => {
    if (!isChecklistsLoading && fetchedChecklists) setChecklists(fetchedChecklists)
  }, [isChecklistsLoading, fetchedChecklists])

  // NCO helpers
  const { selectChecklist, normalizeRows, summarizeProgress, shallowEq } = useNcoHelpers(deal)

  // Derive specific lists
  const integrationChecklist: Checklist[] = useMemo(
    () => (checklists ?? []).filter(chk => chk?.type === 'Integrations'),
    [checklists]
  )
  const seoChecklist: Checklist[] = useMemo(() => (checklists ?? []).filter(chk => chk?.type === 'SEO'), [checklists])
  const onboardingChecklist = useMemo(() => selectChecklist(orgChecklists, organization?.PK, 'Onboarding'), [
    orgChecklists,
    organization?.PK,
    selectChecklist,
  ])

  // Rows & progress
  const seededRows = useMemo(() => normalizeRows(onboardingChecklist), [onboardingChecklist, normalizeRows])
  const completedSet: Set<string> = useMemo(
    () => new Set((seededRows ?? []).filter(r => r.status === 'Done').map(r => r.itemId)),
    [seededRows]
  )
  const integrationRows = useMemo(
    () => (integrationChecklist?.length ? integrationChecklist.flatMap(chk => normalizeRows(chk) ?? []) : []),
    [integrationChecklist, normalizeRows]
  )
  const seoRows = useMemo(() => (seoChecklist?.length ? seoChecklist.flatMap(chk => normalizeRows(chk) ?? []) : []), [
    seoChecklist,
    normalizeRows,
  ])

  const progress = useMemo(() => summarizeProgress(seededRows), [seededRows, summarizeProgress])
  const integrationProgress = useMemo(() => summarizeProgress(integrationRows), [integrationRows, summarizeProgress])

  const mergedRowsForProgress = useMemo(() => [...(seededRows ?? []), ...(integrationRows ?? []), ...(seoRows ?? [])], [
    seededRows,
    integrationRows,
    seoRows,
  ])
  const overallProgress = useMemo(() => summarizeProgress(mergedRowsForProgress), [
    mergedRowsForProgress,
    summarizeProgress,
  ])

  // Channel fractions
  const channelFractions = useMemo<Partial<Record<ChannelKey, number>>>(() => {
    const totals: Record<string, { done: number; total: number }> = {}
    const addFrom = (lists: any[]) => {
      ;(lists ?? []).forEach(chk =>
        (chk?.sections ?? []).forEach((section: any) =>
          (section?.items ?? []).forEach((it: any) => {
            const key = (it?.relatedDealField || '').toLowerCase()
            if (!key) return
            if (!totals[key]) totals[key] = { done: 0, total: 0 }
            totals[key].total += 1
            if (it.status === 'Done') totals[key].done += 1
          })
        )
      )
    }
    addFrom(integrationChecklist)
    addFrom(seoChecklist)
    const acc: Partial<Record<ChannelKey, number>> = {}
    Object.entries(totals).forEach(([k, v]) => {
      acc[k as ChannelKey] = v.total ? v.done / v.total : 0
    })
    return acc
  }, [integrationChecklist, seoChecklist])

  const onboardingFraction = progress.total > 0 ? progress.done / progress.total : 0
  const miaFraction = integrationProgress.total > 0 ? integrationProgress.done / integrationProgress.total : 0

  const [completedCounts, setCompletedCounts] = useState<CompletedCounts>({
    onboardingprogress: 0,
    completeseoform: 0,
    fbpage: 0,
    tiktok: 0,
    linkedin: 0,
    microsoft: 0,
    spotify: 0,
    youtube: 0,
    ga4: 0,
    gtm: 0,
    mia: 0,
  })

  useEffect(() => {
    setCompletedCounts(prev => ({
      onboardingprogress: onboardingFraction ?? prev.onboardingprogress,
      completeseoform: channelFractions.completeseoform ?? prev.completeseoform,
      fbpage: channelFractions.fbpage ?? prev.fbpage,
      tiktok: channelFractions.tiktok ?? prev.tiktok,
      linkedin: channelFractions.linkedin ?? prev.linkedin,
      microsoft: channelFractions.microsoft ?? prev.microsoft,
      spotify: channelFractions.spotify ?? prev.spotify,
      youtube: channelFractions.youtube ?? prev.youtube,
      ga4: channelFractions.ga4 ?? prev.ga4,
      gtm: channelFractions.gtm ?? prev.gtm,
      mia: miaFraction ?? prev.mia,
    }))
  }, [channelFractions, miaFraction, onboardingFraction])

  // Integration form fields
  const [integrationFields, setIntegrationFields] = useState<Record<string, string>>({})
  useEffect(() => {
    if (!organization) return
    setIntegrationFields({
      googleAdsId: organization?.googleAdsId || '',
      metaAccountId: organization?.metaAccountId || '',
      metaPixelId: organization?.metaPixelId || '',
      tiktokAccountId: organization?.tiktokAccountId || '',
      tiktokPixelId: organization?.tiktokPixelId || '',
      simplifiOrgId: organization?.simplifiOrgId || '',
      bingAccountId: organization?.bingAccountId || '',
      bingUetId: organization?.bingUetId || '',
      websiteNotes: organization?.websiteNotes || '',
      trackedConversions: organization?.trackedConversions || '',
    })
  }, [organization])

  // Field change handler - marks explicit user changes
  const handleFieldChange = useCallback(
    async (fieldName: any, newValue: any) => {
      if (!deal) return
      if (deal[fieldName] === newValue) return
      setHasUserChanges(true)
      setPendingDealUpdates(prev => ({ ...prev, [String(fieldName)]: newValue }))
    },
    [deal]
  )

  // Build progress update diff - computes completedCounts on-demand instead of staging
  const buildDealUpdates = useCallback(
    (includeProgress: boolean) => {
      const updates: Record<string, any> = { ...pendingDealUpdates }

      if (includeProgress) {
        // Progress diff
        const prevProgress = deal?.progress ?? {}
        const nextProgress = overallProgress ?? { total: 0, done: 0, pct: 0, byRole: {} }
        if (!shallowEq(prevProgress, nextProgress)) {
          updates.progress = nextProgress
        }

        // completedCounts diff (computed on-demand, not staged)
        const prevCounts = deal?.completedCounts ?? {}
        const nextCounts = completedCounts
        const countsChanged = Object.keys(nextCounts).some(
          key => prevCounts[key as keyof CompletedCounts] !== nextCounts[key as keyof CompletedCounts]
        )
        if (countsChanged) {
          updates.completedCounts = { ...prevCounts, ...nextCounts }
        }
      }

      return updates
    },
    [pendingDealUpdates, deal?.progress, deal?.completedCounts, overallProgress, completedCounts, shallowEq]
  )

  // Create kickoff doc
  const { mutateAsync: createDoc } = useCreateGoogleDoc()
  const handleCreateDocFromTemplate = useCallback(
    async (formData: any) => {
      const token = localStorage.getItem('google_access_token')
      const userId = localStorage.getItem('slack_user_id')
      handleValidateToken(token, userId)
      if (!organization?.googleDriveFolderId) return

      const content = {
        organizationId: organization?.org_uuid,
        PK: organization?.PK,
        SK: organization?.SK,
        organizationName: organization?.name,
        clientIntro: formData?.clientIntro ?? 'N/A',
        website: formData?.website ?? 'N/A',
        portal: formData?.portal ?? 'N/A',
        channels: formData?.channels ?? 'N/A',
        dependencies: formData?.dependencies ?? 'N/A',
        vertical: Array.isArray(formData?.vertical) ? formData.vertical.join(', ') : formData?.vertical ?? 'N/A',
        location: formData?.location ?? 'N/A',
        socials: formData?.socialProfiles ?? 'N/A',
        launchDate: formData?.launchDate ?? 'N/A',
        propertyDetails: formData?.propertyDetails ?? 'N/A',
        billingDetails: formData?.billingDetails ?? 'N/A',
        creatives: formData?.creatives ?? 'N/A',
        extraNotes: formData?.extraNotes ?? 'N/A',
        strategyLink: formData?.strategyLink ?? 'N/A',
        websiteReviewNotes: formData?.websiteReviewNotes ?? 'N/A',
      }

      try {
        const handoffTemplateId = '1EgooUWhtEQ2DTPuv1gpN4iGkw-nvSfTj67-mR5yupOI'
        const { docId, url } = await createDoc({
          folderId: organization?.googleDriveFolderId,
          title: `Handoff Call - ${organization?.name}`,
          templateId: handoffTemplateId,
          content,
        })

        patchOrganizationFieldsInCache(queryClient, organization.PK, organization.SK, [
          { fieldName: 'kickoffCallDocId', newValue: docId },
          { fieldName: 'website', newValue: formData.website },
          { fieldName: 'portal', newValue: formData.portal },
          { fieldName: 'channels', newValue: formData.channels },
          { fieldName: 'dependencies', newValue: formData.dependencies },
          { fieldName: 'location', newValue: formData.location },
          { fieldName: 'socialProfiles', newValue: formData.socialProfiles },
          { fieldName: 'launchDate', newValue: formData.launchDate },
          { fieldName: 'propertyDetails', newValue: formData.propertyDetails },
          { fieldName: 'billingDetails', newValue: formData.billingDetails },
          { fieldName: 'creatives', newValue: formData.creatives },
          { fieldName: 'extraNotes', newValue: formData.extraNotes },
          { fieldName: 'websiteReviewNotes', newValue: formData.websiteReviewNotes },
        ])

        showSnackbar('Google doc created', 'success')
        const openUrl = url || getGoogleEditorUrl(docId, 'application/vnd.google-apps.document')
        window.open(openUrl, '_blank', 'noopener,noreferrer')
      } catch (e) {
        console.error('Error creating google doc: ', e)
        showSnackbar('Error creating google doc', 'error')
      }
    },
    [createDoc, handleValidateToken, organization, patchOrganizationFieldsInCache, queryClient, showSnackbar]
  )

  const handleKickoffCreateDoc = useCallback(
    (data: Record<string, any>) => {
      void handleCreateDocFromTemplate(data)
    },
    [handleCreateDocFromTemplate]
  )

  // Save integration - always includes progress updates
  const saveIntegration = useCallback(() => {
    if (!deal) return
    const updates = buildDealUpdates(true)
    if (!Object.keys(updates).length) return

    updateDealItemsAsync(
      {
        PK: deal.PK,
        SK: deal.SK,
        dealData: { ...deal, ...updates, completedCounts: { ...(updates.completedCounts ?? deal.completedCounts) } },
      },
      {
        onSuccess: () => {
          const fields = [
            ...(updates.progress ? [{ fieldName: 'progress', newValue: updates.progress }] : []),
            ...(updates.completedCounts ? [{ fieldName: 'completedCounts', newValue: updates.completedCounts }] : []),
          ]
          const newDeal = deal as NewDeal
          syncDealFields(queryClient, newDeal, fields)
          setPendingDealUpdates({})
          setHasUserChanges(false)
        },
        onError: () => setPendingDealUpdates({}),
      }
    )
  }, [buildDealUpdates, deal, queryClient, syncDealFields, updateDealItemsAsync])

  // Close drawer - only flush pending updates if user made explicit changes
  const handleClose = useCallback(async () => {
    if (!deal) {
      onRequestClose?.()
      return
    }

    // Only save if user made explicit changes (not just computed progress)
    if (hasUserChanges) {
      const updates = buildDealUpdates(true)

      if (Object.keys(updates).length) {
        try {
          await updateDealItemsAsync({
            PK: deal.PK,
            SK: deal.SK,
            dealData: {
              ...deal,
              ...updates,
              completedCounts: { ...(updates.completedCounts ?? deal.completedCounts) },
            },
          })

          const fields = [
            ...(updates.progress ? [{ fieldName: 'progress', newValue: updates.progress }] : []),
            ...(updates.completedCounts ? [{ fieldName: 'completedCounts', newValue: updates.completedCounts }] : []),
            ...Object.entries(updates)
              .filter(([k]) => k !== 'progress' && k !== 'completedCounts')
              .map(([fieldName, newValue]) => ({ fieldName, newValue })),
          ]

          syncDealFields(queryClient, deal as NewDeal, fields)
        } catch {
          // Silently handle error
        }
      }
    }

    setPendingDealUpdates({})
    setHasUserChanges(false)
    onRequestClose?.()
  }, [hasUserChanges, buildDealUpdates, deal, onRequestClose, queryClient, syncDealFields, updateDealItemsAsync])

  // Comments tab helpers
  const tasks = useMemo(() => {
    if (!allTasks) return []
    return allTasks?.filter(t => t?.PK === PK && t?.isMarketing) ?? []
  }, [allTasks, PK])

  const ncoTasks =
    tasks?.filter(
      t =>
        t?.PK === PK &&
        t?.isMarketing &&
        !t?.SK?.startsWith?.('MONTHLY_') &&
        !t?.SK?.startsWith('SUBTASK_') &&
        t?.masterStatus !== 'Done'
    ) ?? []

  const taskSKs = ncoTasks.map(st => st.SK)

  // Onboarding props
  const checklistProgress = useMemo(
    () => ({
      gtm: completedCounts.gtm ?? 0,
      ga4: completedCounts.ga4 ?? 0,
      fbpage: completedCounts.fbpage ?? 0,
      completeseoform: completedCounts.completeseoform ?? 0,
    }),
    [completedCounts]
  )

  const safeSeededRows: any[] = Array.isArray(seededRows) ? seededRows : []

  const safeOverallProgress = useMemo(() => {
    const p = overallProgress ?? { total: 0, done: 0, pct: 0, byRole: {} }
    const byRole: Record<string, { done: number; total: number }> = Object.fromEntries(
      Object.entries(p.byRole ?? {}).map(([k, v]: [string, any]) => [
        k,
        { done: Number(v?.done ?? 0), total: Number(v?.total ?? 0) },
      ])
    )
    return { total: Number(p.total ?? 0), done: Number(p.done ?? 0), pct: Number(p.pct ?? 0), byRole }
  }, [overallProgress])

  // Stable references for tab config - prevent recreating arrays on every render
  const CONTEXT_FILTERS = useMemo(() => ['nco', 'digital', 'marketing'], [])

  // Tabs config
  const tabs = useMemo(
    () =>
      buildNcoTabsConfig({
        deal,
        organization,
        onboarding: {
          tasklists: Array.isArray(onboardingChecklist) ? onboardingChecklist : [onboardingChecklist].filter(Boolean),
          checklistProgress,
          onChange: handleFieldChange,
          currentChecklist: onboardingChecklist,
          seededRows: safeSeededRows,
          completedSet,
          progress: safeOverallProgress,
        },
        ncoTasks,
        comments: {
          taskSKs,
          context: 'nco',
          expanded: expanded,
          contextFilters: CONTEXT_FILTERS,
          checklistItems: ncoTasks,
        },
        kickoff: {
          onCreateDoc: handleKickoffCreateDoc,
          isCreating: false,
        },
        integration: {
          fields: integrationFields,
          setFields: setIntegrationFields,
          checklist: integrationChecklist,
          onMirrorChecklists: setChecklists,
          onSaved: saveIntegration,
        },
        seoChecklist,
        expanded,
      }),
    [
      deal,
      organization,
      onboardingChecklist,
      checklistProgress,
      handleFieldChange,
      safeSeededRows,
      completedSet,
      safeOverallProgress,
      ncoTasks,
      taskSKs,
      CONTEXT_FILTERS,
      handleKickoffCreateDoc,
      integrationFields,
      integrationChecklist,
      saveIntegration,
      seoChecklist,
      expanded,
    ]
  )

  // Launch Onboard
  const launchOnboard = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault()
      e.stopPropagation()
      if (!organization || !deal) return
      updateFields({
        updates: [
          {
            tableName: 'ORGANIZATIONS_TABLE',
            PK: organization.PK,
            SK: organization.SK,
            fieldName: 'onboardingStatus',
            newValue: 'Launched',
          },
          {
            tableName: 'ORGANIZATIONS_TABLE',
            PK: deal.PK,
            SK: deal.SK,
            fieldName: 'onboardingStatus',
            newValue: 'Launched',
          },
        ],
      })
    },
    [deal, organization, updateFields]
  )

  return (
    <DrawerLayout
      open={isNcoDrawerOpen}
      onClose={handleClose}
      width="74%"
      closeButtonLeft={350}
      expanded={expanded}
      onExpandedChange={setExpanded}
      collapsedHeader={
        <ExpandDisplay
          deal={deal}
          organization={organization}
          overallProgress={overallProgress}
          relatedProducts={relatedProducts}
          launchOnboard={launchOnboard}
          completedCounts={completedCounts}
          onClose={handleClose}
        />
      }
      expandedHeader={<NcoGridHeader deal={deal} organization={organization} />}
    >
      <TabRouting
        key={`${PK}::${SK}`}
        config={tabs}
        onSaveAll={() => {}}
        seoTier={seoTier}
        team_details={{ AM_USERS, DMS_USERS, SEO_USERS, INTEGRATION_USERS }}
        count={commentCount}
      />
    </DrawerLayout>
  )
}

export default Drawer_NCO
